import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, Search, Plus } from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { api, Transaction, TransactionFilters } from '../../lib/api';
import { EmptyState } from '../../components/ui/Spinner';
import { Dropdown } from '../../components/ui/Dropdown';
import { TransactionTable } from '../../components/transactions/TransactionTable';
import { DateFilterControl } from '../../components/transactions/DateFilterControl';
import {
  TX_TYPE_FILTER_OPTIONS,
  type StatusFilter,
} from '../../components/transactions/constants';
import {
  resolveDateInFilter,
  resolveDateOutFilter,
} from '../../components/transactions/dateHelpers';
import { useTransactionListFilters } from '../../components/transactions/useTransactionListFilters';
import { calculateDisplayRate, formatDisplayRate } from '../../lib/format';

const PAGE_LIMIT = 50;

export default function TransactionsPage() {
  const { t } = useTranslation(['transactions', 'common']);
  const { currentEntity } = useEntity();

  const f = useTransactionListFilters();

  const [searchInput, setSearchInput] = useState(f.search);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    setSearchInput(f.search);
  }, [f.search]);

  useEffect(() => {
    if (searchInput === f.search) return;
    const timer = setTimeout(() => f.setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput, f]);

  const activeFilters: TransactionFilters = useMemo(() => {
    return {
      page: f.page,
      limit: PAGE_LIMIT,
      status: f.status === 'all' ? undefined : f.status,
      currencies: f.currencies.length > 0 ? f.currencies : undefined,
      types: f.types.length > 0 ? f.types : undefined,
      clientSearch: f.search || undefined,
      includeArchived: f.archived || undefined,
      employeeId: f.employeeId || undefined,
      ...resolveDateInFilter(f.dateIn),
      ...resolveDateOutFilter(f.dateOut),
    };
  }, [f.page, f.status, f.currencies, f.types, f.search, f.archived, f.employeeId, f.dateIn, f.dateOut]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['transactions', currentEntity?.id, activeFilters],
    queryFn: () => api.transactions.list(currentEntity!.id, activeFilters),
    enabled: !!currentEntity,
  });

  const { data: currenciesData } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.admin.currencies.list(),
    enabled: !!currentEntity,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.admin.users.list(),
    enabled: !!currentEntity,
  });

  const currencies = useMemo(() => {
    if (!currenciesData?.data) return [];
    return currenciesData.data
      .filter((c: any) => c.is_active)
      .map((c: any) => ({ value: c.code, label: c.code }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }, [currenciesData]);

  const employees = useMemo(() => {
    if (!usersData?.data) return [];
    return usersData.data
      .filter((u: any) => u.isActive)
      .map((u: any) => ({ value: u.id, label: u.fullName }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }, [usersData]);

  const hasFilters =
    f.status !== 'all' ||
    f.currencies.length > 0 ||
    f.types.length > 0 ||
    f.employeeId ||
    f.dateIn !== null ||
    f.dateOut !== null ||
    f.archived ||
    f.search;

  const clearFilters = () => {
    f.setStatus('all');
    f.setCurrencies([]);
    f.setTypes([]);
    f.setEmployeeId(null);
    f.setDateIn(null);
    f.setDateOut(null);
    f.setArchived(false);
    f.setSearch('');
    setSearchInput('');
  };

  const handleExport = async () => {
    if (!currentEntity) return;
    try {
      setIsExporting(true);

      const exportFilters: any = { ...activeFilters };
      delete exportFilters.page;
      delete exportFilters.limit;
      const result = await api.transactions.export(currentEntity.id, exportFilters);
      const transactions: Transaction[] = result.data;

      if (transactions.length === 0) {
        alert(t('empty.description'));
        return;
      }

      const headers = [
        t('fields.id', 'ID'),
        t('fields.type', 'Type'),
        t('fields.paymentStatus', 'Status'),
        t('fields.customerName', 'Client Name'),
        'Date Trade In',
        t('fields.employee', 'Employee'),
        t('fields.amountIn', 'Amount In'),
        t('fields.currencyIn', 'Currency In'),
        t('fields.amountOut', 'Amount Out'),
        t('fields.currencyOut', 'Currency Out'),
        t('fields.exchangeRate', 'Exchange Rate'),
        'Oracle Rate',
        'Amount',
        'Currency',
        'Wallet Name',
        'Category',
        'Expected Amount',
        t('fields.debtBalance', 'Debt Balance'),
        'Profit Amount',
        'Profit Currency',
        t('fields.description', 'Description'),
        'Created At',
        'Updated At',
      ];

      const rows = transactions.map((tx) => [
        tx.id,
        tx.type,
        tx.status,
        tx.clientName || '',
        tx.transactionDate ? new Date(tx.transactionDate).toLocaleString() : '',
        tx.employeeName || '',
        tx.amountIn || '',
        tx.currencyIn || '',
        tx.amountOut || '',
        tx.currencyOut || '',
        formatDisplayRate(calculateDisplayRate(tx)),
        tx.oracleRate || '',
        tx.amount || '',
        tx.currency || '',
        tx.walletName || '',
        tx.category || '',
        tx.expectedAmount || '',
        tx.debtAmount || '',
        tx.profitAmount || '',
        tx.profitCurrency || '',
        tx.comment || '',
        tx.createdAt ? new Date(tx.createdAt).toLocaleString() : '',
        tx.updatedAt ? new Date(tx.updatedAt).toLocaleString() : '',
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export:', err);
      alert('Failed to export transactions');
    } finally {
      setIsExporting(false);
    }
  };

  if (!currentEntity) return null;

  const totalPages = data?.meta.totalPages || 1;
  const totalCount = data?.meta.total || 0;
  const displayedCount = data?.data.length || 0;

  return (
    <div className="min-h-screen bg-background p-3 md:p-5">
      <div className="max-w-full mx-auto space-y-3">

        {/* Header */}
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-foreground">{t('title')}</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting}
              className="flex items-center gap-2 px-3 py-2 bg-surface border border-border rounded-lg text-sm text-foreground hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download size={15} />
              {isExporting ? t('common:loading') : t('common:exportCsv', 'Export CSV')}
            </button>
            <Link
              to="/transactions/new"
              className="flex items-center gap-2 px-3 py-2 bg-ctp-teal text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              <Plus size={15} />
              {t('newTransaction')}
            </Link>
          </div>
        </div>

        {/* Filter row 1: status tabs */}
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <div className="inline-flex bg-surface rounded-lg p-1 border border-border shadow-sm">
            {(['all', 'open', 'closed'] as StatusFilter[]).map(s => (
              <button
                key={s}
                className={`px-4 py-1.5 rounded-md text-sm font-medium cursor-pointer transition-all ${
                  f.status === s
                    ? 'bg-surface-hover text-foreground shadow-sm'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
                onClick={() => f.setStatus(s)}
              >
                {s === 'all' ? t('common:all') : s === 'open' ? t('status.open') : t('status.closed')}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Dropdown options={currencies} selected={f.currencies} onChange={f.setCurrencies} placeholder={t('filters.allCurrencies')} multiple />
            <Dropdown options={TX_TYPE_FILTER_OPTIONS} selected={f.types} onChange={f.setTypes} placeholder={t('filters.allTypes')} multiple />
            <Dropdown
              options={employees}
              selected={f.employeeId ? [f.employeeId] : []}
              onChange={vals => f.setEmployeeId(vals[0] ?? null)}
              placeholder={t('filters.allEmployees')}
            />
            <DateFilterControl value={f.dateIn} onChange={f.setDateIn} placeholder={t('filters.dateIn', 'Дата IN')} />
            <DateFilterControl value={f.dateOut} onChange={f.setDateOut} placeholder={t('filters.dateOut', 'Дата OUT')} />
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer px-3 py-2 bg-surface border border-border rounded-lg hover:border-foreground-muted transition-colors">
              <input type="checkbox" checked={f.archived} onChange={e => f.setArchived(e.target.checked)} className="w-4 h-4 accent-ctp-blue" />
              {t('common:includeArchived')}
            </label>
          </div>
        </div>

        {/* Search + count */}
        <div className="flex justify-between items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="relative w-[280px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
              <input
                type="text"
                placeholder={t('filters.searchClient', 'Поиск по клиенту, описанию...')}
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="w-full py-2 pl-9 pr-3 bg-surface border border-border rounded-lg text-sm text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-ctp-blue"
              />
            </div>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="text-xs text-ctp-blue hover:underline whitespace-nowrap"
              >
                {t('common:clear')}
              </button>
            )}
          </div>
          <div className="text-xs text-foreground-muted whitespace-nowrap">
            {t('common:pagination.showing')} <strong className="text-foreground">{displayedCount}</strong> / {totalCount}
          </div>
        </div>

        {/* Table */}
        <div className="bg-surface rounded-xl shadow-sm border border-border overflow-hidden flex flex-col">
          <TransactionTable
            transactions={data?.data ?? []}
            loading={isLoading}
            error={error ? t('common:error') : null}
            emptyState={
              <div className="py-12 flex items-center justify-center">
                <EmptyState
                  title={t('empty.title')}
                  description={t('empty.description')}
                  action={
                    <Link
                      to="/transactions/new"
                      className="flex items-center gap-2 px-4 py-2 bg-ctp-teal text-white rounded-lg text-sm font-semibold hover:opacity-90"
                    >
                      <Plus size={16} />
                      {t('newTransaction')}
                    </Link>
                  }
                />
              </div>
            }
          />

          {!isLoading && !error && (data?.data?.length ?? 0) > 0 && (
            <div className="flex justify-between items-center px-4 py-3 border-t border-border">
              <div className="text-xs text-foreground-muted">
                {t('common:pagination.page')} {f.page} {t('common:pagination.of')} {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  className="px-3 py-1.5 border border-border rounded-lg bg-surface text-xs font-medium text-foreground-muted transition-all hover:bg-surface-hover hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={f.page === 1}
                  onClick={() => f.setPage(f.page - 1)}
                >
                  ← {t('common:prev')}
                </button>
                <button
                  className="px-3 py-1.5 border border-border rounded-lg bg-surface text-xs font-medium text-foreground-muted transition-all hover:bg-surface-hover hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={f.page === totalPages}
                  onClick={() => f.setPage(f.page + 1)}
                >
                  {t('common:next')} →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
